import { SchedulerViewSlot, SchedulerViewSlotProps } from '@progress/kendo-react-scheduler';
import { useMemo } from 'react'; 
import { ProcessedSchedules, Schedule, ScheduleType, UserProviderInfoDetailModel } from "../../../data/entities";
import { toYearMonthDayLocalString } from '../../../utils'; 

interface MonthViewSlotProps extends SchedulerViewSlotProps {
  customStyle?: React.CSSProperties;
  schedules?: Schedule[];
  providers?: UserProviderInfoDetailModel[];
  isAllLocationsView?: boolean;
}


export const MonthViewSlot = (props: MonthViewSlotProps) => {
  const { customStyle, schedules, providers, ...restProps } = props;
  
  const providerIds = getProviderIds(props);
  const scheduleMap = useProcessedSchedules(schedules || [], providerIds);
  
  const isBlocked = schedules && providers 
    ? isDayBlocked(props.start, providerIds, scheduleMap)
    : false;

  return (
    <SchedulerViewSlot
      {...restProps}
      style={{
        ...customStyle,
        ...props.style,
        backgroundImage: isBlocked 
          ? 'repeating-linear-gradient(45deg, transparent, transparent 10px, #e0e0e0 10px, #e0e0e0 20px)'
          : undefined,
        opacity: isBlocked ? 0.5 : 1,
      }}
    />
  );
};

export default MonthViewSlot;


/* Helper Functions */

const isDayBlocked = (
  date: Date,
  providerIds: number[],
  scheduleMap: Map<number, ProcessedSchedules> 
): boolean => {

  if (scheduleMap.size === 0 || providerIds.length === 0) {
    return false;
  }

  const dayOfWeek = date.getDay();
  const dateStr = toYearMonthDayLocalString(date); 

  const hasAvailability = providerIds.some(providerId => {
    const processedSchedules = scheduleMap.get(providerId); 
    if (!processedSchedules) return false;
    
    const activeOverride = processedSchedules.overrides.find(schedule => {
      const effectiveDate = typeof schedule.effectiveDate === 'string' 
        ? schedule.effectiveDate 
        : toYearMonthDayLocalString(schedule.effectiveDate); 
      
      if (dateStr < effectiveDate) return false;
      
      const effectiveEndDate = schedule.effectiveEndDate
        ? typeof schedule.effectiveEndDate === 'string'
          ? schedule.effectiveEndDate
          : toYearMonthDayLocalString(schedule.effectiveEndDate) 
        : null;
      
      return !effectiveEndDate || dateStr <= effectiveEndDate;
    });
    
    const currentDefault = processedSchedules.defaults.find(schedule => {
      const effectiveDate = typeof schedule.effectiveDate === 'string' 
        ? schedule.effectiveDate 
        : toYearMonthDayLocalString(schedule.effectiveDate); 
      return dateStr >= effectiveDate;
    });
    
    const activeSchedule = activeOverride || currentDefault;
    
    if (!activeSchedule || !activeSchedule.scheduleBlocks) return false;
    
    return activeSchedule.scheduleBlocks.some(block => block.dayOfWeek === dayOfWeek);
  });

  const hasActiveSchedule = providerIds.some(providerId => {
    const processedSchedules = scheduleMap.get(providerId); 
    if (!processedSchedules) return false;
    
    const hasStartedDefault = processedSchedules.defaults.some(schedule => {
      const effectiveDate = typeof schedule.effectiveDate === 'string' 
        ? schedule.effectiveDate 
        : toYearMonthDayLocalString(schedule.effectiveDate);
      return dateStr >= effectiveDate;
    });
    
    const hasStartedOverride = processedSchedules.overrides.some(schedule => {
      const effectiveDate = typeof schedule.effectiveDate === 'string' 
        ? schedule.effectiveDate 
        : toYearMonthDayLocalString(schedule.effectiveDate); 
      return dateStr >= effectiveDate;
    });
    
    return hasStartedDefault || hasStartedOverride;
  });

  // Only block if there's an active schedule but no availability for this day of week
  return hasActiveSchedule && !hasAvailability;
};

const getProviderIds = (props: any): number[] => {
  if (props.group?.resources && props.group.resources.length > 0) {
    const resource = props.group.resources[0] as any;
    const groupValue = resource.value || resource.data?.value || resource.providerId || resource.locationId;
    
    if (groupValue) {
      return [groupValue];
    }
  }
  
  return props.providers?.map((p: UserProviderInfoDetailModel) => p.providerId) || [];
};

const useProcessedSchedules = (schedules: Schedule[], providerIds: number[]) => {
  return useMemo(() => {
    const scheduleMap = new Map<number, ProcessedSchedules>();
    
    providerIds.forEach(providerId => {
      const providerSchedules = schedules.filter(s => s.providerId === providerId);
      
      if (providerSchedules.length === 0) {
        scheduleMap.set(providerId, { defaults: [], overrides: [] });
        return;
      }
      
      // Get all default schedules and sort by most recent
      const defaultSchedules = providerSchedules
        .filter(s => s.scheduleType === ScheduleType.Default)
        .sort((a, b) => {
          const dateA = typeof a.effectiveDate === 'string' 
            ? a.effectiveDate 
            : toYearMonthDayLocalString(a.effectiveDate);
          const dateB = typeof b.effectiveDate === 'string' 
            ? b.effectiveDate 
            : toYearMonthDayLocalString(b.effectiveDate);
          return dateB.localeCompare(dateA);
        });
      
      // Get all override schedules and sort by most recent
      const overrideSchedules = providerSchedules
        .filter(s => s.scheduleType === ScheduleType.Override)
        .sort((a, b) => {
          const dateA = typeof a.effectiveDate === 'string' 
            ? a.effectiveDate 
            : toYearMonthDayLocalString(a.effectiveDate);
          const dateB = typeof b.effectiveDate === 'string' 
            ? b.effectiveDate 
            : toYearMonthDayLocalString(b.effectiveDate);
          return dateB.localeCompare(dateA);
        });
      
      scheduleMap.set(providerId, {
        defaults: defaultSchedules,
        overrides: overrideSchedules
      });
    });
    
    return scheduleMap;
  }, [schedules, providerIds.join(',')]);
};